import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchIcon, XIcon } from 'lucide-react'

import { useStore } from '@/contexts/store'

import GeolocationCta from './GeolocationCta'
import type { FlyToCoords } from './SalesNetworkMap'
import { cn } from '@/lib/utils'

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

type MapboxContextItem = {
  id: string
  text: string
  text_it?: string
  short_code?: string
  mapbox_id?: string
}

type MapboxFeature = {
  id: string
  place_name: string
  center: [number, number]
  bbox?: [number, number, number, number]
  context?: MapboxContextItem[]
}

const fetchGeoBbox = async (
  query: string,
  types: string
): Promise<[number, number, number, number] | null> => {
  try {
    const resp = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=1&types=${types}&language=it`
    )
    const data = await resp.json()
    return (
      (data.features?.[0]?.bbox as [number, number, number, number]) ?? null
    )
  } catch {
    return null
  }
}

const fetchReverseGeocode = async (
  lng: number,
  lat: number,
  signal?: AbortSignal
): Promise<string | null> => {
  try {
    const resp = await fetch(
      `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&access_token=${MAPBOX_TOKEN}&language=it&limit=1`,
      { signal }
    )
    const data = await resp.json()
    const props = data?.features?.[0]?.properties
    return props?.full_address ?? props?.place_formatted ?? props?.name ?? null
  } catch {
    return null
  }
}

export type AddressSelection = {
  coords: FlyToCoords
}

export type AddressSearchPanelProps = {
  onAddressSelect?: (_selection: AddressSelection) => void
  onAddressReset?: () => void
  onUseCurrentLocation?: () => void
  typologySortEnabled?: boolean
  onTypologySortChange?: (_enabled: boolean) => void
  userGeolocation: { lat: number; lng: number } | null
  className?: string
}

const AddressSearchPanel = ({
  onAddressSelect,
  onAddressReset,
  onUseCurrentLocation,
  typologySortEnabled = false,
  onTypologySortChange,
  userGeolocation,
  className
}: AddressSearchPanelProps) => {
  const [addressQuery, setAddressQuery] = useState('')
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const { strings } = useStore()

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&language=it`
      )
      const data = await response.json()
      setSuggestions(data.features || [])
      setShowSuggestions(true)
    } catch {
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleAddressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAddressQuery(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 500)
  }

  const handleSuggestionSelect = useCallback(
    async (feature: MapboxFeature) => {
      setAddressQuery(feature.place_name)
      setSuggestions([])
      setShowSuggestions(false)
      setIsLoading(true)

      const center: [number, number] = feature.center
      let resolvedBbox: [number, number, number, number] | undefined =
        feature.bbox
      // True quando il bbox finale proviene dal fetch country-level non
      // italiano: in quel caso la mappa userà lo zoom del bbox ma resterà
      // centrata sul marker (evita la vista "pianeta" per nazioni estese
      // come gli USA).
      let centerOnMarker = false

      try {
        const countryCtx = feature.context?.find((c) =>
          c.id.startsWith('country.')
        )
        const isInItaly = countryCtx?.short_code?.toLowerCase() === 'it'

        if (isInItaly) {
          const regionCtx = feature.context?.find((c) =>
            c.id.startsWith('region.')
          )
          if (regionCtx) {
            const bbox = await fetchGeoBbox(
              regionCtx.text_it ?? regionCtx.text,
              'region'
            )
            if (bbox) resolvedBbox = bbox
          } else if (countryCtx) {
            const bbox = await fetchGeoBbox(
              countryCtx.text_it ?? countryCtx.text,
              'country'
            )
            if (bbox) resolvedBbox = bbox
          }
        } else if (countryCtx) {
          const bbox = await fetchGeoBbox(
            countryCtx.text_it ?? countryCtx.text,
            'country'
          )
          if (bbox) {
            resolvedBbox = bbox
            centerOnMarker = true
          }
        }
        // No country/region in context → use feature.bbox as-is
      } catch {
        // fall back to feature.bbox
      } finally {
        setIsLoading(false)
      }

      onAddressSelect?.({
        coords: {
          lng: center[0],
          lat: center[1],
          zoom: resolvedBbox ? undefined : 14,
          bbox: resolvedBbox,
          centerOnMarker
        }
      })
    },
    [onAddressSelect]
  )

  const handleAddressReset = () => {
    setAddressQuery('')
    setSuggestions([])
    setShowSuggestions(false)
    onAddressReset?.()
    inputRef.current?.focus()
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Reverse geocode user's geolocation to populate the address input,
  // then run the regular forward-search flow on the resolved place name.
  useEffect(() => {
    if (!userGeolocation) return
    const controller = new AbortController()
    ;(async () => {
      setIsLoading(true)
      const placeName = await fetchReverseGeocode(
        userGeolocation.lng,
        userGeolocation.lat,
        controller.signal
      )
      if (controller.signal.aborted) return
      if (!placeName) {
        setIsLoading(false)
        return
      }
      setAddressQuery(placeName)

      // Forward-geocode the resolved place name and auto-select the top
      // result, replicating the standard suggestion-selection flow.
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(placeName)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=false&limit=1&language=it`,
          { signal: controller.signal }
        )
        const data = await response.json()
        const top = data?.features?.[0] as MapboxFeature | undefined
        if (top && !controller.signal.aborted) {
          await handleSuggestionSelect(top)
          return
        }
      } catch {
        // ignore – fall through to clear loading state below
      }
      if (!controller.signal.aborted) setIsLoading(false)
    })()
    return () => controller.abort()
  }, [userGeolocation, handleSuggestionSelect])

  return (
    <div className={cn('flex flex-col gap-spacing-sm', className)}>
      <div className="flex flex-wrap items-center gap-spacing-sm">
        {/* Address input */}
        <div className="relative min-w-[400px] flex-1" ref={wrapperRef}>
          <div
            className={cn(
              'flex h-[4.8rem] w-full rounded-full border border-[rgba(255,255,255,.3)] p-spacing-2xs',
              'flex items-center gap-spacing-xs px-spacing-xs',
              'font-roboto text-d-small',
              'focus-within:border-[rgba(255,255,255,.4)] focus-within:ring-2 focus-within:ring-[rgba(255,255,255,.2)]'
            )}
          >
            <SearchIcon
              size={12}
              className="flex-shrink-0 text-surface-primary-dark"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              type="text"
              value={addressQuery}
              onChange={handleAddressInput}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder={
                strings?.labels?.sales_network_placeholder_input_address ||
                'Cerca un indirizzo...'
              }
              aria-label={strings?.labels?.search_address || 'Cerca indirizzo'}
              aria-controls="address-suggestions"
              aria-expanded={showSuggestions && suggestions.length > 0}
              aria-autocomplete="list"
              role="combobox"
              className="placeholder:text-text-tertiary-dark text-white flex-1 bg-transparent text-[16px] outline-none md:text-[length:inherit]"
            />
            {isLoading && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
            {addressQuery && !isLoading && (
              <button
                type="button"
                aria-label={strings?.labels?.reset_search || 'Rimuovi ricerca'}
                onClick={handleAddressReset}
                className="flex-shrink-0 text-surface-primary-dark"
              >
                <XIcon size={12} aria-hidden="true" />
              </button>
            )}
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <div
              id="address-suggestions"
              role="listbox"
              aria-label={
                strings?.labels?.search_suggestions || 'Suggerimenti di ricerca'
              }
              className={cn(
                'absolute left-0 right-0 top-full z-50 mt-spacing-2xs',
                'bg-black-light',
                'rounded-[2rem] border border-[rgba(255,255,255,.3)]',
                'max-h-[250px] overflow-auto'
              )}
            >
              {suggestions.map((feature) => (
                <button
                  key={feature.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSuggestionSelect(feature)}
                  className={cn(
                    'w-full text-left',
                    'text-d-small',
                    'px-[1.5rem] py-[1.2rem]',
                    'hover:bg-white/5',
                    'text-text-primary-dark'
                  )}
                >
                  {feature.place_name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex h-[48px] items-center">
          <GeolocationCta onClick={onUseCurrentLocation} />
        </div>
      </div>
    </div>
  )
}

export default AddressSearchPanel
